import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import FairPriceView from '@/components/polymer/FairPriceView';
import UsitcChart from '@/components/polymer/UsitcChart';
import FeedstocksTab from '@/components/polymer/FeedstocksTab';
import RefineryRatesView from '@/components/polymer/RefineryRatesView';
import PolymerDashboardLayout from '@/components/polymer/PolymerDashboardLayout';
import type { PolymerDashboardConfig } from '@/types/polymer-dashboard.types';
import { getCachedRefineryRates } from '@/services/marketIntelService';

const PET_CONFIG: PolymerDashboardConfig = {
  polymer: 'PET',
  polymerFullName: 'PET',
  defaultTab: 'overview',
  tabs: [
    { key: 'overview', label: 'Overview' },
    { key: 'fair-price', label: 'Fair Resin Price' },
    { key: 'feedstocks', label: 'Feedstocks' },
    { key: 'usitc', label: 'PET USITC' },
    { key: 'refinery-rates', label: 'Refinery Rates' },
  ],
};

const ChartPanel: React.FC<{ title: string; children: React.ReactNode; className?: string }> = ({ title, children, className }) => (
  <div className={`bg-rs-off-black/60 backdrop-blur-sm border border-rs-jet-light/50 rounded-xl shadow-lg shadow-black/20 p-5 ${className || ''}`}>
    <h3 className="text-sm font-medium text-rs-off-white/70 uppercase tracking-wider mb-4">{title}</h3>
    {children}
  </div>
);

const PETDashboard: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const [refineryData, setRefineryData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const hasFetched = useRef(false);
  const [isSmallOverview, setIsSmallOverview] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 750px)').matches,
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 750px)');
    setIsSmallOverview(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsSmallOverview(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (hasFetched.current || !isAuthenticated) return;
    hasFetched.current = true;
    setLoading(true);
    getCachedRefineryRates()
      .then(data => { setRefineryData(data); })
      .catch(() => setRefineryData([]))
      .finally(() => setLoading(false));
  }, [isAuthenticated]);

  const renderContent = (activeTab: string) => {
    if (activeTab === 'overview') {
      return (
        <div className={`flex flex-col gap-6 ${isSmallOverview ? 'p-0' : ''}`}>
          {isSmallOverview ? (
            <FairPriceView polymer="PET" labelMode="overview" fullBleed />
          ) : (
            <ChartPanel title="Fair Resin Price">
              <FairPriceView polymer="PET" labelMode="overview" />
            </ChartPanel>
          )}
          {isSmallOverview ? (
            <FeedstocksTab polymer="PET" fullBleed />
          ) : (
            <ChartPanel title="Feedstocks">
              <FeedstocksTab polymer="PET" />
            </ChartPanel>
          )}
          {isSmallOverview ? (
            <RefineryRatesView refineryData={refineryData} />
          ) : (
            <ChartPanel title="Refinery Rates">
              <RefineryRatesView refineryData={refineryData} />
            </ChartPanel>
          )}
        </div>
      );
    }

    switch (activeTab) {
      case 'fair-price':
        return <FairPriceView polymer="PET" labelMode="tab" fullBleed />;
      case 'feedstocks':
        return <FeedstocksTab polymer="PET" fullBleed />;
      case 'usitc':
        return <UsitcChart polymer="PET" />;
      case 'refinery-rates':
        return <RefineryRatesView refineryData={refineryData} />;
      default:
        return null;
    }
  };

  if (isLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-rs-black">
        <Loader2 className="animate-spin opacity-30 w-16 h-16 text-rs-green" />
      </div>
    );
  }

  return (
    <PolymerDashboardLayout config={PET_CONFIG} renderContent={renderContent} />
  );
};

export default PETDashboard;
